import { UNASSIGNED_NAMESPACE_VALUE } from '@/components/namespaces/namespaceLabels';

type Namespaced = { namespaceId: string | null };

/** One heading of the Workbooks list: a namespace's Workbooks, or the ungrouped ones (`namespace` null). */
export type WorkbookNamespaceGroup<W extends Namespaced> = {
  namespace: { id: string; name: string; key: string } | null;
  workbooks: W[];
};

/**
 * The Workbooks list's groups, in the org's namespace order with the ungrouped
 * last and empty groups dropped. `filter` holds namespace ids and
 * UNASSIGNED_NAMESPACE_VALUE; empty keeps every Workbook. A Workbook whose
 * namespace is not (yet) listed counts as ungrouped.
 */
export function workbookNamespaceGroups<W extends Namespaced>(
  workbooks: readonly W[],
  namespaces: readonly { id: string; name: string; key: string }[],
  filter: readonly string[],
): WorkbookNamespaceGroup<W>[] {
  const listed = new Set(namespaces.map((namespace) => namespace.id));
  const groupOf = (workbook: W) =>
    workbook.namespaceId && listed.has(workbook.namespaceId)
      ? workbook.namespaceId
      : UNASSIGNED_NAMESPACE_VALUE;
  const kept = workbooks.filter(
    (workbook) => filter.length === 0 || filter.includes(groupOf(workbook)),
  );
  const groups: WorkbookNamespaceGroup<W>[] = namespaces.map((namespace) => ({
    namespace,
    workbooks: kept.filter((workbook) => groupOf(workbook) === namespace.id),
  }));
  groups.push({
    namespace: null,
    workbooks: kept.filter(
      (workbook) => groupOf(workbook) === UNASSIGNED_NAMESPACE_VALUE,
    ),
  });
  return groups.filter((group) => group.workbooks.length > 0);
}
